import { useEffect, useState, useCallback } from 'react';
import { generateCallReferenceId } from '../../services/sap-cti.service';
import { OUTBOUND_DIAL_EVENT, OutboundDialEventDetail } from '../../services/sap-context';
import { beginCallTrace, logCallActivity } from '../../services/call-log/call-log.service';
import { logger } from '../../utils/logger';
import { unlockAcwUi } from './acw-ui-lock';
import { OutboundCallInfo } from './types';

interface UseOutboundDialOptions {
  setLastCallerPhone: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useOutboundDial({ setLastCallerPhone }: UseOutboundDialOptions) {
  const [outboundCallInfo, setOutboundCallInfo] = useState<OutboundCallInfo | null>(null);
  const [isOutboundCall, setIsOutboundCall] = useState(false);

  useEffect(() => {
    function handleOutboundDial(event: Event) {
      const customEvent = event as CustomEvent<OutboundDialEventDetail>;
      const { phone, contactId } = customEvent.detail;
      logger.debug('[Widget] Outbound dial event received:', { phone, contactId });

      // A new outbound leg is starting — release ACW lock so early SDK events are not dropped.
      unlockAcwUi();

      const referenceId = generateCallReferenceId();
      beginCallTrace();
      logCallActivity('CALL_RECEIVED', {
        direction: 'OUTBOUND',
        phone,
        sapContactId: contactId,
        referenceId,
      });
      setOutboundCallInfo({
        phone,
        sapContactId: contactId,
        startedAt: new Date().toISOString(),
        referenceId,
      });
      setIsOutboundCall(true);
      setLastCallerPhone(phone);
      // Do not mark the contact as Linked here — that must wait for a real CTI ACCEPT
      // (Link to call / auto-open). Seeding openedSapContacts caused false "Linked" UI and
      // a spurious END when the agent later linked a different match.
    }

    window.addEventListener(OUTBOUND_DIAL_EVENT, handleOutboundDial);
    return () => {
      window.removeEventListener(OUTBOUND_DIAL_EVENT, handleOutboundDial);
    };
  }, [setLastCallerPhone]);

  const resetOutboundState = useCallback(() => {
    setOutboundCallInfo(null);
    setIsOutboundCall(false);
  }, []);

  return {
    outboundCallInfo,
    isOutboundCall,
    resetOutboundState,
  };
}
